import type { PlacedLocation } from '../types/map.types';

interface ChallengeRecord {
  entityIds: Set<string>;
  rewardLocation: PlacedLocation;
  roomIndex: number;
  onClear: (reward: PlacedLocation) => void;
  onRestore: () => void;
}

class ChallengeService {
  private challenges = new Map<string, ChallengeRecord>();

  private locationKey(loc: PlacedLocation): string {
    return `${loc.x}:${loc.y}`;
  }

  public isActive(): boolean {
    return this.challenges.size > 0;
  }

  public register(
    triggerLocation: PlacedLocation,
    entityIds: string[],
    rewardLocation: PlacedLocation,
    roomIndex: number,
    onClear: (reward: PlacedLocation) => void,
    onRestore: () => void,
  ): void {
    const key = this.locationKey(triggerLocation);
    this.challenges.set(key, {
      entityIds: new Set(entityIds),
      rewardLocation,
      roomIndex,
      onClear,
      onRestore,
    });
  }

  public onEntityRemoved(entityId: string): void {
    for (const [key, record] of this.challenges) {
      if (!record.entityIds.has(entityId)) {
        continue;
      }
      record.entityIds.delete(entityId);
      if (record.entityIds.size === 0) {
        this.challenges.delete(key);
        record.onRestore();
        record.onClear(record.rewardLocation);
      }
    }
  }

  /**
   * Called on every player move. If the player is no longer inside the room of
   * an active challenge (escaped via teleport/knockback through the sealed
   * doors), reopen the doors and drop the challenge with no reward. The spawned
   * monsters are left untouched — they become normal floor monsters that keep
   * pursuing. The trigger was already marked used at activation, so no re-trigger.
   */
  public checkEscape(currentRoomIndex: number): void {
    for (const [key, record] of this.challenges) {
      if (currentRoomIndex !== record.roomIndex) {
        this.challenges.delete(key);
        record.onRestore();
      }
    }
  }

  public reset(): void {
    this.challenges.clear();
  }
}

export const challengeService = new ChallengeService();
